import { useEffect, useMemo, useState } from 'react';
import type { UserDeletionImpact } from '../features/users/types';

export type OwnedResourcesAction = 'transfer' | 'delete';

interface UserOption {
  id: number;
  email: string;
}

interface UserDeleteOwnedResourcesDialogProps {
  open: boolean;
  userId: number;
  userEmail: string;
  impact: UserDeletionImpact;
  userOptions: UserOption[];
  submitting: boolean;
  errorMessage: string | null;
  onClose: () => void;
  onConfirm: (payload: { ownedResourcesAction: OwnedResourcesAction; transferToUserId?: number }) => void;
}

export function UserDeleteOwnedResourcesDialog({
  open,
  userId,
  userEmail,
  impact,
  userOptions,
  submitting,
  errorMessage,
  onClose,
  onConfirm,
}: UserDeleteOwnedResourcesDialogProps) {
  const [action, setAction] = useState<OwnedResourcesAction>('transfer');

  const transferCandidates = useMemo(
    () => userOptions.filter((u) => u.id !== userId),
    [userOptions, userId],
  );

  const [transferToUserId, setTransferToUserId] = useState<number>(() => transferCandidates[0]?.id ?? 0);

  useEffect(() => {
    if (open) {
      const canTransfer = transferCandidates.length > 0;
      setAction(canTransfer ? 'transfer' : 'delete');
      setTransferToUserId(transferCandidates[0]?.id ?? 0);
    }
  }, [open, transferCandidates]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && open && !submitting) onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, submitting, onClose]);

  if (!open) return null;

  const total = impact.assessmentCount + impact.communicationMatrixCount;

  const handleSubmit = () => {
    if (action === 'transfer') {
      if (!transferToUserId || transferToUserId === userId) return;
      onConfirm({ ownedResourcesAction: 'transfer', transferToUserId });
      return;
    }
    onConfirm({ ownedResourcesAction: 'delete' });
  };

  const transferDisabled = action === 'transfer' && (!transferToUserId || transferCandidates.length === 0);

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
      role="presentation"
      onClick={() => !submitting && onClose()}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="delete-user-dialog-title"
        className="max-h-[90vh] w-full max-w-lg overflow-y-auto rounded-lg border border-border bg-background p-6 shadow-lg"
        onClick={(e) => e.stopPropagation()}
      >
        <h2 id="delete-user-dialog-title" className="text-lg font-semibold text-foreground">
          Eliminar usuario
        </h2>
        <p className="mt-2 text-sm text-muted-foreground">
          <span className="font-medium text-foreground">{userEmail}</span> es dueño de recursos en la plataforma.
          Los cambios en planillas compartidas (Teable) no se borran al transferir: solo cambia el dueño del
          formulario en el sistema.
        </p>

        <ul className="mt-3 list-disc space-y-1 pl-5 text-sm text-foreground">
          <li>
            Formularios / encuestas (assessments):{' '}
            <strong>{impact.assessmentCount}</strong>
          </li>
          <li>
            Registros de matriz de comunicación:{' '}
            <strong>{impact.communicationMatrixCount}</strong>
          </li>
        </ul>

        {impact.assessmentsPreview.length > 0 && (
          <div className="mt-3 rounded-md border border-border bg-muted/30 p-3 text-xs text-muted-foreground">
            <p className="font-medium text-foreground">Ejemplos de formularios</p>
            <ul className="mt-1 list-disc pl-4">
              {impact.assessmentsPreview.map((a) => (
                <li key={a.id}>{a.title}</li>
              ))}
            </ul>
          </div>
        )}

        <fieldset className="mt-4 space-y-3 border-0 p-0">
          <legend className="text-sm font-medium text-foreground">¿Qué hacer con esos datos ({total})?</legend>
          <label className="flex cursor-pointer items-start gap-2 text-sm">
            <input
              type="radio"
              name="owned-action"
              checked={action === 'transfer'}
              onChange={() => setAction('transfer')}
              disabled={submitting || transferCandidates.length === 0}
              className="mt-1"
            />
            <span>
              Transferir la propiedad a otro usuario (recomendado). El nuevo dueño podrá gestionarlos con su
              configuración Teable.
            </span>
          </label>
          <label className="flex cursor-pointer items-start gap-2 text-sm">
            <input
              type="radio"
              name="owned-action"
              checked={action === 'delete'}
              onChange={() => setAction('delete')}
              disabled={submitting}
              className="mt-1"
            />
            <span>
              Eliminar esos formularios y registros de matriz del sistema. Se borran también respuestas y vínculos
              en base de datos; bases en Teable pueden quedar huérfanas si existían.
            </span>
          </label>
        </fieldset>

        {action === 'transfer' && transferCandidates.length > 0 && (
          <div className="mt-4">
            <label htmlFor="transfer-to-user" className="block text-sm font-medium text-foreground">
              Usuario destino
            </label>
            <select
              id="transfer-to-user"
              className="mt-1 w-full rounded-md border border-input bg-background px-3 py-2 text-sm text-foreground"
              value={transferToUserId || ''}
              onChange={(e) => setTransferToUserId(Number(e.target.value))}
              disabled={submitting}
            >
              {transferCandidates.map((u) => (
                <option key={u.id} value={u.id}>
                  {u.email} (id {u.id})
                </option>
              ))}
            </select>
          </div>
        )}

        {action === 'transfer' && transferCandidates.length === 0 && (
          <p className="mt-3 text-sm text-destructive">
            No hay otro usuario al cual transferir. Cree un usuario o elija eliminar los datos asociados.
          </p>
        )}

        {errorMessage && (
          <p className="mt-3 text-sm text-destructive" role="alert">
            {errorMessage}
          </p>
        )}

        <div className="mt-6 flex justify-end gap-2">
          <button
            type="button"
            className="rounded-md border border-border bg-background px-4 py-2 text-sm font-medium text-foreground hover:bg-muted"
            onClick={onClose}
            disabled={submitting}
          >
            Cancelar
          </button>
          <button
            type="button"
            className="rounded-md bg-destructive px-4 py-2 text-sm font-medium text-destructive-foreground hover:bg-destructive/90 disabled:opacity-50"
            onClick={handleSubmit}
            disabled={submitting || transferDisabled}
          >
            {submitting ? 'Eliminando…' : 'Confirmar eliminación'}
          </button>
        </div>
      </div>
    </div>
  );
}
